// Batch amount resolution: the whole money model for a capture batch,
// as pure functions. The review screen collects inputs and renders what
// this returns — it never decides an amount itself (same split as
// lib/batch.ts and lib/selectors.ts, so every rule below is unit-tested
// headlessly rather than through a DOM).
//
// WHY CENTS. Every number in this file is an INTEGER of cents. The
// round-trip split is real arithmetic — full = total - defaultRate — and
// in floats that is how you ship a load priced $324.99999999. Dollars
// exist only at the two boundaries: parsing what the driver typed (or
// what the numeric(12,2) column returned) on the way in, and formatting
// on the way out.

// Which rule priced a ticket. Persisted onto field_provenance.amount so
// a later reader can tell a rate-filled load from a handwritten one —
// "$75" that a human wrote and "$75" the app assumed are the same number
// and very different facts.
export type AmountSource = "manual" | "split-full" | "split-empty" | "handwritten" | "default";

export interface AmountTicket {
  id: string;
  moveType: string | null;
  containerNo: string | null;
  // What the vision model read off the ticket, in cents; null when it
  // found no handwritten figure.
  handwrittenCents: number | null;
}

// A pair the DRIVER has confirmed. Auto-detection only proposes (see
// detectRoundTripPairs) — nothing here moves a total until confirmed.
export interface ConfirmedPair {
  fullId: string;
  emptyId: string;
  // The one number the driver enters for the pair. Null until they do;
  // the legs then fall back to their own rules rather than splitting.
  totalCents: number | null;
}

export interface ResolvedAmount {
  ticketId: string;
  amountCents: number | null;
  source: AmountSource;
  // Set when the pair needs a human look. Rendered on the round-trip card.
  warn?: string;
}

export interface ResolveOptions {
  defaultRateCents: number | null;
  confirmedPairs: ConfirmedPair[];
  // ticketId -> cents. Set by tapping an amount and typing.
  manualOverrides: Record<string, number>;
}

// --- Move-type predicates ------------------------------------------------

const EMPTY_MOVES = ["EMPTY_IN", "EMPTY_OUT"];
const FULL_MOVES = ["FULL_IN", "FULL_OUT"];

export function isEmptyMove(moveType: string | null): boolean {
  return moveType !== null && EMPTY_MOVES.includes(moveType);
}

export function isFullMove(moveType: string | null): boolean {
  return moveType !== null && FULL_MOVES.includes(moveType);
}

function isInbound(moveType: string | null): boolean {
  return moveType === "EMPTY_IN" || moveType === "FULL_IN";
}

function isOutbound(moveType: string | null): boolean {
  return moveType === "EMPTY_OUT" || moveType === "FULL_OUT";
}

// --- Cents <-> dollars at the boundaries ---------------------------------

// Parse a money string into integer cents. Accepts what both sides
// actually produce: the numeric(12,2) column's "385.00" / "385", and
// what a driver types ("385", "$385", "1,450.50").
//
// Parsed by STRING, not by Number(x) * 100 — the multiply is where
// 4.60 becomes 459.99999999999994 and a penny goes missing. Returns null
// on anything that isn't a clean non-negative amount, including more
// than two decimal places, which is not a thing money has.
export function parseCents(value: string | null | undefined): number | null {
  if (value === null || value === undefined) return null;
  const cleaned = value.trim().replace(/^\$/, "").replace(/,/g, "");
  if (cleaned === "") return null;
  const m = /^(\d+)(?:\.(\d{1,2}))?$/.exec(cleaned);
  if (!m) return null;
  const whole = Number(m[1]);
  const frac = (m[2] ?? "").padEnd(2, "0");
  if (!Number.isSafeInteger(whole)) return null;
  return whole * 100 + Number(frac);
}

// Cents -> the dollars number the PATCH body carries (the server coerces
// it into numeric(12,2)). Integer division, so 32500 -> 325 exactly.
export function centsToDollars(cents: number): number {
  return Math.trunc(cents / 100) + (cents % 100) / 100;
}

// Cents -> "$325.00" for display. en-US pinned for the same reason
// lib/format.ts pins it: identical rendering on every device and in test.
export function formatCents(cents: number | null): string | null {
  if (cents === null) return null;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(centsToDollars(cents));
}

// Cents -> the bare "325.00" a number input wants as its value.
export function centsToInputValue(cents: number | null): string {
  if (cents === null) return "";
  return centsToDollars(cents).toFixed(2);
}

// --- Round-trip detection ------------------------------------------------

export interface CandidatePair {
  containerNo: string;
  fullId: string;
  emptyId: string;
}

// Propose round trips within one batch: the same container going out and
// coming back, one leg loaded and one leg empty.
//
// Deliberately strict. A container with three legs, or two legs the same
// direction, or two loaded legs, yields NOTHING rather than a guess —
// mis-pairing two tickets silently rewrites both their amounts, which is
// a worse failure than making the driver link them by hand. This is why
// the three-different-containers sample batch must produce zero pairs.
export function detectRoundTripPairs(tickets: AmountTicket[]): CandidatePair[] {
  const byContainer = new Map<string, AmountTicket[]>();
  for (const t of tickets) {
    const key = (t.containerNo ?? "").trim().toUpperCase();
    if (key === "") continue; // An unread container number pairs with nothing.
    const bucket = byContainer.get(key);
    if (bucket) bucket.push(t);
    else byContainer.set(key, [t]);
  }

  const pairs: CandidatePair[] = [];
  for (const [containerNo, group] of byContainer) {
    if (group.length !== 2) continue;
    const [a, b] = group as [AmountTicket, AmountTicket];

    // Exactly one in and one out.
    const directionOk =
      (isInbound(a.moveType) && isOutbound(b.moveType)) ||
      (isOutbound(a.moveType) && isInbound(b.moveType));
    if (!directionOk) continue;

    // Exactly one loaded and one empty.
    const full = isFullMove(a.moveType) ? a : isFullMove(b.moveType) ? b : null;
    const empty = isEmptyMove(a.moveType) ? a : isEmptyMove(b.moveType) ? b : null;
    if (!full || !empty || full.id === empty.id) continue;

    pairs.push({ containerNo, fullId: full.id, emptyId: empty.id });
  }
  return pairs;
}

// --- The precedence -------------------------------------------------------

// Resolve every ticket in the batch to an amount plus the rule that
// produced it. Precedence, highest first:
//
//   1. manual      — the driver typed this one; nothing overrides a human.
//   2. split-*     — a leg of a CONFIRMED round trip: empty leg takes the
//                    default rate, full leg takes total - default, so the
//                    two legs sum to the one total the driver entered.
//   3. handwritten — what the model read off the ticket.
//   4. default     — the batch flat rate.
//
// Side-effect free and total: every input ticket gets exactly one output
// row, in input order.
export function resolveBatchAmounts(
  tickets: AmountTicket[],
  opts: ResolveOptions,
): ResolvedAmount[] {
  const { defaultRateCents, confirmedPairs, manualOverrides } = opts;

  // ticketId -> its split result. Computed per pair up front so both legs
  // agree on one arithmetic, and a leg whose pair can't split cleanly
  // simply isn't in here and falls through to the rules below.
  const splits = new Map<string, { amountCents: number; source: AmountSource; warn?: string }>();
  const known = new Set(tickets.map((t) => t.id));
  const claimed = new Set<string>();

  for (const pair of confirmedPairs) {
    // Defensive: a pair naming a ticket that isn't in this batch, or a
    // ticket already spoken for by an earlier pair, is not a split.
    if (!known.has(pair.fullId) || !known.has(pair.emptyId)) continue;
    if (pair.fullId === pair.emptyId) continue;
    if (claimed.has(pair.fullId) || claimed.has(pair.emptyId)) continue;

    // A split needs both numbers. Without a rate there is nothing to give
    // the empty leg; without a total there is nothing to divide. Either
    // way the legs stay ordinary tickets rather than getting a made-up
    // number, and the pair says why.
    if (defaultRateCents === null) {
      claimed.add(pair.fullId);
      claimed.add(pair.emptyId);
      continue;
    }
    if (pair.totalCents === null) {
      claimed.add(pair.fullId);
      claimed.add(pair.emptyId);
      continue;
    }

    claimed.add(pair.fullId);
    claimed.add(pair.emptyId);

    const remainder = pair.totalCents - defaultRateCents;
    if (remainder < 0) {
      // Guardrail: the total does not even cover the empty leg. Never
      // emit a negative load — clamp and flag, because the real fix is
      // the driver correcting the total or the rate, not us guessing.
      const warn = `Total ${formatCents(pair.totalCents)} is less than the ${formatCents(
        defaultRateCents,
      )} default rate — check this pair.`;
      splits.set(pair.emptyId, { amountCents: defaultRateCents, source: "split-empty", warn });
      splits.set(pair.fullId, { amountCents: 0, source: "split-full", warn });
      continue;
    }

    splits.set(pair.emptyId, { amountCents: defaultRateCents, source: "split-empty" });
    splits.set(pair.fullId, { amountCents: remainder, source: "split-full" });
  }

  return tickets.map((ticket): ResolvedAmount => {
    // 1. Manual — a number the driver typed outranks every rule.
    const manual = manualOverrides[ticket.id];
    if (manual !== undefined) {
      return { ticketId: ticket.id, amountCents: manual, source: "manual" };
    }

    // 2. Confirmed round-trip leg.
    const split = splits.get(ticket.id);
    if (split) {
      return {
        ticketId: ticket.id,
        amountCents: split.amountCents,
        source: split.source,
        ...(split.warn ? { warn: split.warn } : {}),
      };
    }

    // 3. What the model read.
    if (ticket.handwrittenCents !== null) {
      return {
        ticketId: ticket.id,
        amountCents: ticket.handwrittenCents,
        source: "handwritten",
      };
    }

    // 4. The batch rate — or nothing, when no rate is set. `default` is
    // the rule that applied either way; a null amount means it had
    // nothing to give, and the row stays flagged as needing an amount.
    return { ticketId: ticket.id, amountCents: defaultRateCents, source: "default" };
  });
}

// Index the resolution by ticket id — what the UI wants when rendering a
// card it already holds.
export function byTicketId(resolved: ResolvedAmount[]): Record<string, ResolvedAmount> {
  const out: Record<string, ResolvedAmount> = {};
  for (const r of resolved) out[r.ticketId] = r;
  return out;
}
